// frontend/src/views/AddPrinterView.jsx

import React, { useState } from 'react';
import QRDisplay from '../components/QRDisplay';
import { 
    Plus, 
    Printer, 
    Cloud, 
    RefreshCw, 
    Calendar, 
    // ?? FIX: Use Safe Aliases for Icons to prevent crash
    CircleCheck as CheckCircle, 
    CircleAlert as AlertCircle 
} from 'lucide-react';

function AddPrinterView() {
    // Default date string YYYY-MM-DD
    const todayStr = new Date().toISOString().split('T')[0];

    const [formData, setFormData] = useState({
        name: '', model: '', serial_number: '', initial_print_hours: '',
        // ?? RESTORED: Date Fields
        last_biweekly_date: todayStr,
        last_monthly_date: todayStr,
        last_quarterly_date: todayStr
    });
    
    const [newPrinter, setNewPrinter] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [syncLoading, setSyncLoading] = useState(false);
    const [syncMessage, setSyncMessage] = useState('');

    const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setNewPrinter(null);

        if (!formData.name || !formData.serial_number) {
            setError('Name and Serial Number required.');
            setLoading(false); return;
        }

        try {
            const res = await fetch('/api/printers', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message);
            setNewPrinter(data);
            // Reset form but keep dates as today
            setFormData({ 
                name: '', model: '', serial_number: '', initial_print_hours: '',
                last_biweekly_date: todayStr, last_monthly_date: todayStr, last_quarterly_date: todayStr
            }); 
        } catch (err) { setError(err.message); } 
        finally { setLoading(false); }
    };

    const handleSync = async () => {
        setSyncLoading(true);
        setSyncMessage('Syncing...');
        try {
            const res = await fetch('/api/printers/sync', { method: 'POST' });
            const result = await res.json();
            setSyncMessage(res.ok ? `Synced ${result.records_synced} records.` : `Error: ${result.message}`);
        } catch (err) { setSyncMessage(`Error: ${err.message}`); } 
        finally { setSyncLoading(false); }
    };

    return (
        <div className="container">
            <div className="card">
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '1rem' }}>
                    <Plus size={24} color="var(--primary)" />
                    <h2 style={{ margin: 0 }}>Add New Printer</h2>
                </div>
                <p>Register a new device to generate its QR code.</p>
                
                {error && (
                    <div className="text-red" style={{padding:'10px', background:'#fee2e2', borderRadius:'8px', marginBottom:'10px', display:'flex', alignItems:'center', gap:'8px'}}>
                        <AlertCircle size={18} /> {error}
                    </div>
                )}

                <form onSubmit={handleSubmit}>
                    <div className="form-section" style={{ marginBottom: '15px', padding: '10px', background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                        <h3 style={{fontSize: '1rem', marginBottom: '10px'}}>Device Info</h3>
                        <label>Printer Name</label>
                        <input type="text" name="name" placeholder="e.g. T1, PolyCarbon" value={formData.name} onChange={handleChange} />
                        
                        <label>Model</label>
                        <input type="text" name="model" placeholder="e.g. Bambu Lab X1C" value={formData.model} onChange={handleChange} />
                        
                        <label>Serial Number</label>
                        <input type="text" name="serial_number" placeholder="Device S/N" value={formData.serial_number} onChange={handleChange} />
                        
                        <label>Current Hours</label>
                        <input type="number" name="initial_print_hours" placeholder="0" step="0.1" value={formData.initial_print_hours} onChange={handleChange} />
                    </div>

                    {/* ?? RESTORED: Maintenance Baseline Section */}
                    <div className="form-section" style={{ marginBottom: '15px', padding: '10px', background: '#f0fdfa', border: '1px solid #ccfbf1' }}>
                        <h3 style={{fontSize: '1rem', marginBottom: '10px', display: 'flex', alignItems: 'center', gap:'5px'}}>
                            <Calendar size={16}/> Initial Maintenance Baseline
                        </h3>
                        <p style={{fontSize: '0.8rem', marginBottom: '10px'}}>Set the last time these were done (Defaults to Today).</p>
                        
                        <label>Last Biweekly (14 Days)</label>
                        <input type="date" name="last_biweekly_date" value={formData.last_biweekly_date} onChange={handleChange} />
                        
                        <label>Last Monthly (30 Days)</label>
                        <input type="date" name="last_monthly_date" value={formData.last_monthly_date} onChange={handleChange} />
                        
                        <label>Last Quarterly (90 Days)</label>
                        <input type="date" name="last_quarterly_date" value={formData.last_quarterly_date} onChange={handleChange} />
                    </div>

                    <button type="submit" disabled={loading} style={{ display: 'flex', gap: '8px' }}>
                        {loading ? <RefreshCw className="spin" size={20}/> : <Plus size={20} />}
                        {loading ? 'Adding...' : 'Generate QR Code'}
                    </button>
                </form>
            </div>

            {newPrinter && (
                <div className="card text-center" style={{borderTop: '4px solid var(--success)'}}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', color: 'var(--success)', marginBottom: '10px' }}>
                        <CheckCircle size={24} />
                        <h3 style={{ margin: 0 }}>{newPrinter.name} Added!</h3>
                    </div>
                    <QRDisplay qrKey={newPrinter.qr_code_key} printerName={newPrinter.name} />
                </div>
            )}

            <div className="card">
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '1rem' }}>
                    <Cloud size={24} color="#0284c7" /> {/* Sky Blue */}
                    <h3 style={{ margin: 0 }}>Google QMS Sync</h3>
                </div>
                <p>Push unsynced records to the audit log.</p>
                
                <button className="secondary" onClick={handleSync} disabled={syncLoading} style={{ display: 'flex', gap: '8px' }}>
                    <RefreshCw size={20} className={syncLoading ? 'spin' : ''} />
                    {syncLoading ? 'Syncing...' : 'Run Sync Now'}
                </button>
                
                {syncMessage && (
                    <div style={{ marginTop: '15px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {syncMessage.includes('Error') ? <AlertCircle size={18} color="var(--danger)"/> : <CheckCircle size={18} color="var(--success)"/>}
                        <strong>{syncMessage}</strong>
                    </div>
                )}
            </div>
            
            <style>{`.spin { animation: spin 1s linear infinite; } @keyframes spin { 100% { transform: rotate(360deg); } }`}</style>
        </div>
    );
}

export default AddPrinterView;